import { ref, computed, nextTick, onBeforeUnmount, getCurrentInstance } from 'vue';

const AUDIO_NS = 'globalAudioPlayback';

/**
 * @typedef {Object} UseGlobalAudioPlaybackBridgeOptions
 * @property {() => void} [onPlaybackEnded] 当前列表自然播放完毕时回调（对应播放器 `ended` 事件）
 * @property {() => void} [onBarClose] 控制栏通过 UI 关闭时回调（对应 `@close`；不含业务侧 `close()` 主动调用）
 * @property {() => void} [onManualBarClose] 用户手动点击控制栏关闭按钮时回调（对应 `@manualClose`；不含播完自动关闭）
 */

/** @type {Set<() => void>} */
const playbackEndedListeners = new Set();

/** @type {Set<() => void>} */
const barCloseListeners = new Set();

/** @type {Set<() => void>} */
const manualBarCloseListeners = new Set();

/**
 * 依次调用已注册的监听器，单个失败不影响其余回调。
 * @param {Set<() => void>} listeners
 */
const notifyListeners = (listeners) => {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (err) {
      console.error('[useGlobalAudioPlaybackBridge] listener threw', err);
    }
  });
};

/**
 * 全局唯一：根上 `<CustomAudioPlaybackController ref="…" />` 绑定的实例。
 * 任意组件内再调 `useGlobalAudioPlaybackBridge(store)` 时复用此 ref，无需 provide/inject。
 */
const globalAudioControllerRef = ref(null);

/**
 * 在下一帧对「已挂载的」全局音频条实例执行回调（避免与异步子组件 ref 竞态）。
 * @param {import('vue').Ref<object|null>} controllerRef - `CustomAudioPlaybackController` 的模板 ref
 * @param {(c: object) => void} fn - 拿到实例后调用的方法
 */
const runOnGlobalAudioController = (controllerRef, fn) => {
  nextTick(() => {
    const c = controllerRef.value;
    if (c) {
      fn(c);
    }
  });
};

/**
 * 根级与业务级均可调用：聚合 Vuex → 播放器 props，并通过**同一** `globalAudioControllerRef` 驱动实例。
 * 在 `App.vue` 中必须把返回值里的 `globalAudioControllerRef` 绑到模板 `ref`，子页面再调本 hook 即可 `play`/`pause` 等。
 *
 * @param {import('vuex').Store} store - `useStore()` 返回值
 * @param {UseGlobalAudioPlaybackBridgeOptions} [options] - 可选生命周期回调
 */
export function useGlobalAudioPlaybackBridge(store, options = {}) {
  const { onPlaybackEnded, onBarClose, onManualBarClose } = options;

  if (typeof onPlaybackEnded === 'function') {
    playbackEndedListeners.add(onPlaybackEnded);
  }
  if (typeof onBarClose === 'function') {
    barCloseListeners.add(onBarClose);
  }
  if (typeof onManualBarClose === 'function') {
    manualBarCloseListeners.add(onManualBarClose);
  }

  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      if (typeof onPlaybackEnded === 'function') {
        playbackEndedListeners.delete(onPlaybackEnded);
      }
      if (typeof onBarClose === 'function') {
        barCloseListeners.delete(onBarClose);
      }
      if (typeof onManualBarClose === 'function') {
        manualBarCloseListeners.delete(onManualBarClose);
      }
    });
  }

  const globalAudioVisible = computed(() => store.state[AUDIO_NS].visible);

  const globalAudioBarProps = computed(() => {
    const s = store.state[AUDIO_NS];
    return {
      label: s.label,
      initializing: s.initializing,
      placeholderText: s.placeholderText,
      srcList: s.srcList,
      trackCurrent: s.trackCurrent,
      totalTracks: s.totalTracks,
      fetchAudioLoading: s.fetchAudioLoading,
      autoplay: s.autoPlay,
      autoNextPlay: s.autoNextPlay
    };
  });

  const play = () => {
    runOnGlobalAudioController(globalAudioControllerRef, (c) => c.startPlay());
  };

  const pause = () => {
    runOnGlobalAudioController(globalAudioControllerRef, (c) => c.pausePlayback());
  };

  /** @param {number} trackIndex - 列表下标，从 0 起 */
  const playTrack = (trackIndex) => {
    runOnGlobalAudioController(globalAudioControllerRef, (c) => {
      if (typeof c.playTrackAt === 'function') {
        c.playTrackAt(trackIndex);
      }
    });
  };

  /** 先停声再同步 Vuex，避免仅隐藏条仍播放 */
  const close = () => {
    const c = globalAudioControllerRef.value;
    if (c) {
      c.pausePlayback();
    }
    store.dispatch(`${AUDIO_NS}/close`);
  };

  /** 与模板 `@close` 一致：先通知 UI 关闭监听器，再同步停播与 Vuex */
  const onGlobalAudioBarClose = () => {
    notifyListeners(barCloseListeners);
    close();
  };

  /** 与模板 `@manualClose` 一致 */
  const onGlobalAudioBarManualClose = () => {
    notifyListeners(manualBarCloseListeners);
  };

  /** 与模板 `@ended` 一致 */
  const onGlobalAudioPlaybackEnded = () => {
    notifyListeners(playbackEndedListeners);
    store.dispatch(`${AUDIO_NS}/syncPlaying`, false);
  };

  /** 与模板 `@trackChange` 一致：同步播放器内部当前曲目下标 */
  const onGlobalAudioTrackChange = (trackIndex) => {
    store.dispatch(`${AUDIO_NS}/syncTrackCurrent`, trackIndex);
  };

  /** 与模板 `@play` 一致：同步全局播放态 */
  const onGlobalAudioPlay = () => {
    store.dispatch(`${AUDIO_NS}/syncPlaying`, true);
  };

  /** 与模板 `@pause` 一致：同步全局播放态 */
  const onGlobalAudioPause = () => {
    store.dispatch(`${AUDIO_NS}/syncPlaying`, false);
  };

  const toggleInitializing = (initializing) => {
    store.dispatch(`${AUDIO_NS}/toggleInitializing`, initializing);
  };

  const init = (payload) => {
    store.dispatch(`${AUDIO_NS}/init`, payload);
  };

  return {
    globalAudioControllerRef,
    globalAudioVisible,
    globalAudioBarProps,
    onGlobalAudioBarClose,
    onGlobalAudioBarManualClose,
    onGlobalAudioPlaybackEnded,
    onGlobalAudioTrackChange,
    onGlobalAudioPlay,
    onGlobalAudioPause,
    toggleInitializing,
    init,
    play,
    pause,
    playTrack,
    close
  };
}
